// src/pages/Home.jsx
import { useScrollReveal } from "../hooks/useScrollReveal";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { Brain, MessageCircle, Shield, BookOpen, Star, ArrowLeft, ArrowRight } from "lucide-react";

import SEO from "../components/SEO";

export default function Home() {
  const { t, lang } = useLanguage();
  useScrollReveal();

  const features = lang === "ar" ? [
    { icon: <Brain size={28} />, title: "اختبار ADHD مجاني", desc: "اختبار علمي موثق يساعدك على فهم وضع طفلك" },
    { icon: <MessageCircle size={28} />, title: "استشارات متخصصة", desc: "تواصل مباشر مع أطباء وأخصائيين نفسيين معتمدين" },
    { icon: <Shield size={28} />, title: "بيئة آمنة وسرية", desc: "خصوصيتك محمية وبياناتك مشفرة بالكامل" },
    { icon: <BookOpen size={28} />, title: "موارد تعليمية", desc: "مكتبة شاملة من التمارين والإرشادات لأولياء الأمور" }
  ] : [
    { icon: <Brain size={28} />, title: "Free ADHD Assessment", desc: "Evidence-based assessment to understand your child's situation" },
    { icon: <MessageCircle size={28} />, title: "Expert Consultations", desc: "Direct contact with certified doctors and psychologists" },
    { icon: <Shield size={28} />, title: "Safe & Confidential", desc: "Your privacy is protected and data fully encrypted" },
    { icon: <BookOpen size={28} />, title: "Educational Resources", desc: "Comprehensive library of exercises and guidance for parents" }
  ];

  const testimonials = [
    { name: "أم عبدالله", text: "ساعدتني منصة نظم كثيراً في فهم ابني وكيفية التعامل معه", rating: 5 },
    { name: "والد محمد", text: "الأطباء متجاوبون جداً والتمارين المقترحة مفيدة جداً لطفلي", rating: 5 },
    { name: "أم نورة", text: "استشارة مجانية ممتازة، حصلت على إجابات لأسئلتي كلها", rating: 5 }
  ];

  const ArrowIcon = lang === "ar" ? ArrowLeft : ArrowRight;

  return (
    <div className="fade-in">
      <SEO 
        title={lang === "ar" ? "الرئيسية" : "Home"} 
        description={lang === "ar" ? "منصة نظم لدعم وتوجيه الأسر والأطفال ذوي اضطراب فرط الحركة وتشتت الانتباه ADHD في الجزائر." : "Nodom platform for supporting families and children with ADHD in Algeria."}
        canonicalUrl="https://www.nodom.site/"
      />
      {/* Hero Section */}
      <section style={{
        background: "linear-gradient(135deg, var(--color-forest-lt) 0%, var(--color-sky-lt) 100%)",
        padding: "5rem 0 4rem",
        position: "relative",
        overflow: "hidden"
      }}>
        {/* Decorative circles */}
        <div style={{
          position: "absolute", top: "-60px", left: lang === "ar" ? "-60px" : "auto",
          right: lang === "ar" ? "auto" : "-60px",
          width: 220, height: 220,
          borderRadius: "50%",
          background: "rgba(34,139,34,0.07)",
          pointerEvents: "none"
        }} />
        <div style={{
          position: "absolute", bottom: "-40px", right: lang === "ar" ? "-40px" : "auto",
          left: lang === "ar" ? "auto" : "-40px",
          width: 160, height: 160,
          borderRadius: "50%",
          background: "rgba(135,206,235,0.12)",
          pointerEvents: "none"
        }} />

        <div className="container" style={{ position: "relative" }}>
          <div style={{ maxWidth: 640 }}>
            <div className="badge badge-green" style={{ marginBottom: "1rem" }}>
              {lang === "ar" ? "منصة دعم ADHD رقم 1# في الجزائر" : "Algeria #1 ADHD Support Platform"}
            </div>
            <h1 style={{ fontSize: "clamp(2rem,5vw,3rem)", lineHeight: 1.2, marginBottom: "1.25rem" }}>
              {t("heroTitle")}
            </h1>
            <p style={{ fontSize: "1.1rem", lineHeight: 1.8, marginBottom: "2rem", color: "var(--color-gray-600)" }}>
              {t("heroSubtitle")}
            </p>
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <Link to="/quiz" className="btn btn-primary btn-lg">
                {t("startQuiz")}
                <ArrowIcon size={18} />
              </Link>
              <a href="#about"
                className="btn btn-outline btn-lg"
                style={{ background: "rgba(255,255,255,0.7)" }}
                onClick={e => {
                  e.preventDefault();
                  document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {t("learnMore")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="reveal" style={{ background: "var(--color-forest)", padding: "1.5rem 0" }}>
        <div className="container">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "1rem", textAlign: "center" }}>
            {[
              { num: "500+", label: lang === "ar" ? "أسرة مستفيدة" : "Families Helped" },
              { num: "3+",  label: lang === "ar" ? "طبيب متخصص" : "Specialists" },
              { num: "98%",  label: lang === "ar" ? "رضا الأسر" : "Family Satisfaction" }
            ].map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "#fff" }}>{s.num}</div>
                <div style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.75)", marginTop: "0.2rem" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: "4rem 0" }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: "2.5rem" }}>
            <h2>{lang === "ar" ? "لماذا تختار نظم؟" : "Why Choose Nodom?"}</h2>
          </div>
          <div className="grid-2" style={{ gap: "1.25rem" }}>
            {features.map((f, i) => (
              <div key={i} className={`card reveal reveal-delay-${i + 1}`} style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
                <div style={{
                  width: 52, height: 52, borderRadius: "var(--radius-md)",
                  background: "var(--color-forest-lt)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "var(--color-forest)", flexShrink: 0
                }}>
                  {f.icon}
                </div>
                <div>
                  <h4 style={{ marginBottom: "0.4rem" }}>{f.title}</h4>
                  <p style={{ margin: 0, fontSize: "0.9rem" }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={{ padding: "4rem 0", background: "var(--color-gray-50)" }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: "2.5rem" }}>
            <h2>{lang === "ar" ? "كيف يعمل نظم؟" : "How Nodom Works"}</h2>
          </div>
          <div className="grid-3">
            {(lang === "ar" ? [
              { num: "1", title: "أجرِ الاختبار", desc: "أجب عن 12 سؤالاً علمياً عن سلوك طفلك" },
              { num: "2", title: "احصل على نتيجة", desc: "استقبل تقريراً فورياً بمستوى الخطر ونصائح أولية" },
              { num: "3", title: "تواصل مع متخصص", desc: "اختر الاستشارة المجانية أو المدفوعة حسب احتياجك" }
            ] : [
              { num: "1", title: "Take the Assessment", desc: "Answer 12 scientific questions about your child's behavior" },
              { num: "2", title: "Get Your Results", desc: "Receive an instant report with risk level and initial tips" },
              { num: "3", title: "Connect with a Specialist", desc: "Choose free or paid consultation based on your needs" }
            ]).map((step, i) => (
              <div key={i} className={`reveal reveal-delay-${i + 1}`} style={{ textAlign: "center", padding: "1.5rem" }}>
                <div style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: "var(--color-forest)", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1.2rem", fontWeight: 800, margin: "0 auto 1rem"
                }}>
                  {step.num}
                </div>
                <h4 style={{ marginBottom: "0.5rem" }}>{step.title}</h4>
                <p style={{ fontSize: "0.9rem" }}>{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center" style={{ marginTop: "2rem" }}>
            <Link to="/quiz" className="btn btn-primary btn-lg">
              {t("startQuiz")} <ArrowIcon size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: "4rem 0" }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: "2.5rem" }}>
            <h2>{lang === "ar" ? "آراء الأسر" : "Family Reviews"}</h2>
          </div>
          <div className="grid-3">
            {testimonials.map((t, i) => (
              <div key={i} className={`card reveal reveal-delay-${i + 1}`}>
                <div style={{ display: "flex", marginBottom: "0.75rem" }}>
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={16} fill="var(--color-earth)" style={{ color: "var(--color-earth)" }} />
                  ))}
                </div>
                <p style={{ fontSize: "0.925rem", fontStyle: "italic", marginBottom: "0.75rem" }}>"{t.text}"</p>
                <span style={{ fontWeight: 700, fontSize: "0.875rem", color: "var(--color-forest)" }}>{t.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* About Us */}
      <section id="about" style={{ padding: "5rem 0", background: "#3b5882" }}>
        <div className="container">
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "4rem",
            alignItems: "center"
          }}>

            {/* Logo Side */}
            <div className="reveal-left" style={{ display: "flex", justifyContent: "center" }}>
              <img
                src="/Logo_withoutBG.png"
                alt="نظم"
                style={{
                  width: "100%",
                  maxWidth: 280,
                  filter: "drop-shadow(0 8px 32px rgba(34,139,34,0.35))"
                }}
              />
            </div>

            {/* Text Side */}
            <div className="reveal-right">
              <div className="badge badge-green" style={{ marginBottom: "1rem" }}>
                {lang === "ar" ? "من نحن" : "About Us"}
              </div>
              <h2 style={{ color: "#fff", marginBottom: "1.25rem", lineHeight: 1.4 }}>
                {lang === "ar"
                  ? "نظم — رحلة من المتاهة إلى الوضوح"
                  : "Nodom — A Journey from Maze to Clarity"}
              </h2>
              <p style={{ color: "rgba(255,255,255,0.7)", lineHeight: 2, marginBottom: "1rem" }}>
                {lang === "ar"
                  ? "نظم منصة جزائرية متخصصة في دعم أسر الأطفال المصابين باضطراب فرط الحركة وتشتت الانتباه (ADHD). نؤمن أن كل طفل يحمل في داخله طريقاً للخروج من المتاهة — نحن هنا لمساعدتك على إيجاده."
                  : "Nodom is an Algerian platform specialized in supporting families of children with ADHD. We believe every child carries a path out of the maze — we're here to help you find it."}
              </p>
              <p style={{ color: "rgba(255,255,255,0.7)", lineHeight: 2, marginBottom: "1.75rem" }}>
                {lang === "ar"
                  ? "فريقنا من الأطباء والأخصائيين النفسيين يعمل على مدار الأسبوع لتقديم اختبارات موثوقة، استشارات مخصصة، وخطط علاجية تناسب كل طفل."
                  : "Our team of doctors and psychologists works around the week to provide reliable assessments, personalized consultations, and treatment plans tailored to each child."}
              </p>

              {/* Mini Stats */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "1rem" }}>
                {[
                  { num: lang === "ar" ? "2026" : "2026", label: lang === "ar" ? "سنة التأسيس" : "Founded" },
                  { num: lang === "ar" ? "3+" : "+3",    label: lang === "ar" ? "مختصين" : "Specialists" },
                  { num: lang === "ar" ? "غرداية" : "Ghardaia", label: lang === "ar" ? "الجزائر" : "Algeria" }
                ].map((s, i) => (
                  <div key={i} style={{
                    padding: "1rem",
                    background: "rgba(255,255,255,0.06)",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    textAlign: "center"
                  }}>
                    <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#fff" }}>
                      {s.num}
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)", marginTop: "0.2rem" }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section style={{
        padding: "4rem 0",
        background: "linear-gradient(135deg, var(--color-forest) 0%, var(--color-forest-dk) 100%)",
        textAlign: "center"
      }}>
        <div className="container">
          <h2 style={{ color: "#fff", marginBottom: "1rem" }}>
            {lang === "ar" ? "ابدأ رحلتك مع طفلك اليوم" : "Start Your Journey With Your Child Today"}
          </h2>
          <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "1.05rem", marginBottom: "2rem" }}>
            {lang === "ar" ? "الاختبار مجاني تماماً وبدون تسجيل" : "The assessment is completely free, no registration needed"}
          </p>
          <Link to="/quiz" className="btn btn-lg" style={{ background: "#fff", color: "var(--color-forest)", fontWeight: 700 }}>
            {t("startQuiz")} <ArrowIcon size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
